// AI を使うゲームの口（ゲームの appful.ai.generate）。関数 aiGenerate を呼ぶ（docs/AI.md）。
// ゲームの id は、一覧が開いているゲームの物を使う（ゲームから来た値は使わない。bridge.ts が渡す）。
// 鍵はどこにも無い。AI を呼ぶのは関数で、上限・回数・非常停止も関数が見る。ここは失敗を SDK の code に直すだけ。
import { AI_ERROR_CODES, type AiErrorCode, type AiGenerateParams, type AiGenerateResult } from "@appful/sdk/protocol";
import { httpsCallable } from "firebase/functions";
import { whenSignedIn } from "./auth";
import { functions } from "./firebase";

/** ゲームに返す失敗（code は SDK の AI_ERROR_CODES） */
export class AiError extends Error {
  constructor(
    public code: AiErrorCode,
    message: string,
  ) {
    super(message);
  }
}

// 関数は Vertex AI を 20 秒まで待つ。起きるまでの時間も見て、少し長めに待つ
const CALL_TIMEOUT_MS = 45_000;

/** 関数の失敗の理由（HttpsError の details.reason）。無ければ空 */
function reasonOf(e: unknown): string {
  const details = (e as { details?: unknown } | null)?.details;
  const reason = typeof details === "object" && details !== null ? (details as { reason?: unknown }).reason : undefined;
  return typeof reason === "string" ? reason : "";
}

/** 関数の失敗を SDK の code に直す。ログインが要る（ゲストのまま）は not-enabled。形の誤り・通信の失敗・時間切れ・知らない理由は unavailable */
function toAiError(e: unknown): AiError {
  const reason = reasonOf(e);
  const message = e instanceof Error ? e.message : "AI error";
  if ((AI_ERROR_CODES as readonly string[]).includes(reason)) return new AiError(reason as AiErrorCode, message);
  return new AiError(reason === "signin-required" ? "not-enabled" : "unavailable", message);
}

async function call(gameId: string, params: AiGenerateParams): Promise<AiGenerateResult> {
  const res = await httpsCallable<unknown, AiGenerateResult>(functions, "aiGenerate", { timeout: CALL_TIMEOUT_MS })({ gameId, ...params });
  const data = res.data;
  if (typeof data?.text !== "string") throw new AiError("unavailable", "bad answer");
  return data.json === undefined ? { text: data.text } : { text: data.text, json: data.json };
}

/**
 * AI に頼む。ログインが要るゲーム（設定の requireSignIn）でゲストなら、ログインの画面を 1 回出し、ログインしたら頼み直す。
 * ゲストのままなら not-enabled
 */
export async function generate(gameId: string, params: AiGenerateParams, requestSignIn: () => Promise<void>): Promise<AiGenerateResult> {
  try {
    return await call(gameId, params);
  } catch (e) {
    if (e instanceof AiError) throw e;
    if (reasonOf(e) !== "signin-required") {
      if (!reasonOf(e)) console.error("[ai] generate failed", e);
      throw toAiError(e);
    }
  }
  await requestSignIn();
  if (!(await whenSignedIn())) throw new AiError("not-enabled", "sign in required");
  try {
    return await call(gameId, params);
  } catch (e) {
    throw e instanceof AiError ? e : toAiError(e);
  }
}
